import _ from 'lodash';
import DDLogs from '../../DDLogs';
import { budgetVersion } from './actionsDone';

// The budget localStorage cache: an optimistic edit survives a reload while the server keeps returning the same
// version (it hasn't rebuilt the cashflow yet) and the entry hasn't expired.
// Two keys, one month each: the current month (riseup.budgets, as before) and the last month edited besides it. An
// edit on another month replaces the one before it, never the current month, and a save never reads the stored
// budgets first (they can be several MB). When both don't fit (iOS caps localStorage at about 5MB, and a big budget
// takes over half of it), the current month is kept alone.
// A small index (riseup.budgets.index) says which month each key holds, with its version and expiry. A read parses a
// key only when it holds a fresh copy of a month asked for, and an expired copy is dropped without being parsed, so the
// other month's copy doesn't stay behind when that month is never loaded again.
// The months one edit changed are linked in the index: when one of them is lost (a failed write, or a later edit taking
// its key), the others are dropped on read too, so a reload never shows the edit in one month and not in the other.
// The keys are slots: after the month turns, the new current month may be the one in riseup.budgets.other, and it
// keeps that key until a newer copy replaces it.
// The redesign keeps its own copy (elsa-v3/src/utils/budgetCache.ts, riseup.budgets.v3).

const KEY = 'riseup.budgets';
const OTHER_MONTH_KEY = 'riseup.budgets.other';
const INDEX_KEY = 'riseup.budgets.index';
const KEYS = [KEY, OTHER_MONTH_KEY];
// The redesign's keys: v3 has no logout of its own, so clearBudgetCache removes them too. Renaming them in
// elsa-v3/src/utils/budgetCache.ts without changing them here would leave its copies after logout.
const V3_KEYS = ['riseup.budgets.v3', 'riseup.budgets.v3.other', 'riseup.budgets.v3.index'];
const EXPIRATION_TIME = 30 * 60 * 1000; // 30 minutes in milliseconds

function _parseFromStorage(key) {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    return {};
  }
}

function _setInStorage(key, json) {
  try {
    localStorage.setItem(key, json);
    return true;
  } catch (error) {
    return false;
  }
}

function _removeFromStorage(key) {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    // Storage unavailable: nothing is stored
  }
}

function _isInStorage(key) {
  try {
    return localStorage.getItem(key) !== null;
  } catch (error) {
    return false;
  }
}

function _getObjectSizeUTF8(json) {
  return new TextEncoder().encode(json).length;
}

function _isStoredDataExpired({ expirationTime }) {
  return Date.now() > expirationTime;
}

// An index that can't be written clears the cache: its stale entries could drop or skip a fresh copy, and without it
// the copies would lose their links (a full localStorage, where nothing cached is worth a half-shown edit)
function _writeIndex(index) {
  if (!_setInStorage(INDEX_KEY, JSON.stringify(index))) {
    [...KEYS, INDEX_KEY].forEach(_removeFromStorage);
    return false;
  }
  return true;
}

function _setIndexEntry(key, entry) {
  return _writeIndex({ ..._parseFromStorage(INDEX_KEY), [key]: entry });
}

function _removeKey(key) {
  _removeFromStorage(key);
  const index = _parseFromStorage(INDEX_KEY);
  if (index[key]) {
    _writeIndex(_.omit(index, key));
  }
}

function _keyHolding(index, budgetDate) {
  return _.find(KEYS, key => index[key] && index[key].budgetDate === budgetDate);
}

// A month is written over its own copy. Otherwise the current month goes to riseup.budgets, unless the month turned
// while the new month's copy was in the other key, and any other month to the key not holding the current month.
function _keyFor(index, budgetDate, currentBudgetDate) {
  const currentKey = _keyHolding(index, currentBudgetDate) || KEY;
  const otherKey = currentKey === KEY ? OTHER_MONTH_KEY : KEY;
  return _keyHolding(index, budgetDate) || (budgetDate === currentBudgetDate ? currentKey : otherKey);
}

// The month a key holds: from the index, or by parsing a copy stored before the index (or whose index write failed)
function _monthIn(key, index) {
  if (index[key]) {
    return index[key].budgetDate;
  }
  return _isInStorage(key) ? _.first(_.keys(_parseFromStorage(key))) : undefined;
}

export function clearBudgetCache() {
  [...KEYS, INDEX_KEY, ...V3_KEYS].forEach(_removeFromStorage);
}

// Returns whether the month is stored. A month saved again keeps the months any fresh copy of it was linked to: its
// copy still holds that earlier edit.
export function saveBudgetToStorage(budgetDate, budget, currentBudgetDate) {
  const version = budgetVersion(budget);
  const expirationTime = Date.now() + EXPIRATION_TIME;
  const json = JSON.stringify({ [budgetDate]: { budget, version, expirationTime } });
  const index = _parseFromStorage(INDEX_KEY);
  const key = _keyFor(index, budgetDate, currentBudgetDate);
  const otherKey = key === KEY ? OTHER_MONTH_KEY : KEY;
  const linked = _.union(..._.map(_.filter(index, entry => entry.budgetDate === budgetDate && !_isStoredDataExpired(entry)), 'linked'));
  const indexEntry = _.isEmpty(linked) ? { budgetDate, version, expirationTime } : { budgetDate, version, expirationTime, linked };
  if (_setInStorage(key, json)) {
    _setIndexEntry(key, indexEntry);
    return true;
  }
  if (budgetDate === currentBudgetDate) {
    // Both don't fit: the current month is kept alone
    const hadOtherMonth = _isInStorage(otherKey);
    _removeKey(otherKey);
    if (hadOtherMonth && _setInStorage(key, json)) {
      _setIndexEntry(key, indexEntry);
      DDLogs.info('Saved only the current budget to localStorage', { budgetDate, objectSize: _getObjectSizeUTF8(json) });
      return true;
    }
  } else {
    // This month doesn't fit next to the current month: its older copy goes, the current month stays
    _removeKey(key);
    if (_monthIn(otherKey, _parseFromStorage(INDEX_KEY)) === currentBudgetDate) {
      DDLogs.info('Saved only the current budget to localStorage', { budgetDate, objectSize: _getObjectSizeUTF8(json) });
      return false;
    }
  }
  // Not even one month fits: nothing is kept, so a reload can't serve a copy older than this edit
  _removeKey(key);
  DDLogs.info('Failed to save budget to localStorage:', { budgetDate, objectSize: _getObjectSizeUTF8(json) });
  return false;
}

// The months one edit changed are kept together or not at all: each is linked to the others, and if one wasn't stored
// (or its link can't be recorded), none is kept
function _linkMonths(budgetDates) {
  if (budgetDates.length < 2) {
    return;
  }
  const index = _parseFromStorage(INDEX_KEY);
  const keys = budgetDates.map(budgetDate => _.find(KEYS, key => index[key] && index[key].budgetDate === budgetDate));
  const linkedIndex = _.every(keys) && _.reduce(keys, (result, key, i) => ({
    ...result,
    [key]: { ...index[key], linked: _.union(index[key].linked, _.without(budgetDates, budgetDates[i])) },
  }), index);
  if (!linkedIndex || !_writeIndex(linkedIndex)) {
    budgetDates.forEach(removeBudgetFromStorage);
  }
}

export function removeBudgetFromStorage(budgetDate) {
  const index = _parseFromStorage(INDEX_KEY);
  KEYS.forEach(key => {
    if (_monthIn(key, index) === budgetDate) {
      _removeKey(key);
    }
  });
}

// An edit that changed other months besides the presented one (a move to another month, a category removed from the
// later months). The presented month and the current month are stored by the mutations; one other month is stored
// here, and the months are linked. When the edit changed more months than the cache holds, none of them is kept, the
// current month included, so a reload shows them all as the server has them, consistently, rather than the edit in
// one month and not in another.
export function saveMonthsChangedWithEdit(presentedBudgetDate, changedBudgets, currentBudgetDate) {
  const changedDates = _.uniq([presentedBudgetDate, ..._.map(changedBudgets, 'budgetDate')]);
  if (_.without(changedDates, currentBudgetDate).length > 1) {
    changedDates.forEach(removeBudgetFromStorage);
    return;
  }
  const otherMonth = _.find(changedBudgets, ({ budgetDate }) => budgetDate !== currentBudgetDate && budgetDate !== presentedBudgetDate);
  if (otherMonth) {
    saveBudgetToStorage(otherMonth.budgetDate, otherMonth, currentBudgetDate);
  }
  _linkMonths(changedDates);
}

// A copy linked to a month that's no longer stored (fresh) holds only half of an edit: dropped, and then any copy
// linked to it, until the rest hold together
function _dropBrokenLinks(index) {
  const isBroken = key => {
    const entry = index[key];
    const heldKeys = _.filter(KEYS, other => other !== key && index[other] && !_isStoredDataExpired(index[other]));
    const heldMonths = _.map(heldKeys, other => index[other].budgetDate);
    return entry && _.some(entry.linked, budgetDate => !heldMonths.includes(budgetDate));
  };
  let broken = _.find(KEYS, isBroken);
  while (broken) {
    _removeKey(broken);
    delete index[broken]; // eslint-disable-line no-param-reassign
    broken = _.find(KEYS, isBroken);
  }
}

// The keys worth parsing for these budgets. From the index alone: a key holding a month not asked for is left as is,
// and one whose copy is expired, of another version than the server's or half of a lost edit is dropped without being
// parsed.
function _keysToRead(apiBudgets) {
  const index = _parseFromStorage(INDEX_KEY);
  _dropBrokenLinks(index);
  const apiByDate = _.keyBy(_.compact(apiBudgets), 'budgetDate');
  return _.filter(KEYS, key => {
    const entry = index[key];
    if (!entry) {
      // Stored before the index: read as before
      return _isInStorage(key);
    }
    if (_isStoredDataExpired(entry)) {
      _removeKey(key);
      return false;
    }
    const apiBudget = apiByDate[entry.budgetDate];
    if (!apiBudget) {
      return false;
    }
    if (budgetVersion(apiBudget) !== entry.version) {
      DDLogs.info('Using api version budget', {
        budgetDate: entry.budgetDate, versionCreated: budgetVersion(apiBudget), storedVersion: entry.version,
      });
      _removeKey(key);
      return false;
    }
    return true;
  });
}

// A budget from the server, or its stored copy while the server hasn't rebuilt it (same version, not expired)
function _withRecentChanges(apiBudget, budgetWithRecentChanges) {
  if (!apiBudget || !budgetWithRecentChanges) {
    return { budget: apiBudget, isStale: false };
  }
  const versionCreated = budgetVersion(apiBudget);
  if (versionCreated === budgetWithRecentChanges.version && !_isStoredDataExpired(budgetWithRecentChanges)) {
    DDLogs.info('Using stored budget', { budgetDate: apiBudget.budgetDate, versionCreated, storedVersion: budgetWithRecentChanges.version });
    return { budget: budgetWithRecentChanges.budget, isStale: false };
  }

  if (versionCreated !== budgetWithRecentChanges.version) {
    DDLogs.info('Using api version budget', { budgetDate: apiBudget.budgetDate, versionCreated, storedVersion: budgetWithRecentChanges.version });
  }
  // A newer version or an expired copy: the running version is used, and the stored copy removed
  return { budget: apiBudget, isStale: true };
}

// Each key is parsed at most once for all the budgets, and only when it holds a fresh copy of one of them
export function getBudgetsWithRecentChanges(apiBudgets) {
  const keysToRead = _keysToRead(apiBudgets);
  if (_.isEmpty(keysToRead)) {
    return apiBudgets;
  }
  const stored = keysToRead.map(key => ({ key, budgets: _parseFromStorage(key) }));
  const staleKeys = new Set();
  const budgets = _.map(apiBudgets, apiBudget => {
    // The newest copy: a month can be under both keys after the month turned
    const holder = apiBudget && _.maxBy(
      _.filter(stored, ({ budgets: storedBudgets }) => storedBudgets[apiBudget.budgetDate]),
      ({ budgets: storedBudgets }) => storedBudgets[apiBudget.budgetDate].expirationTime,
    );
    const { budget, isStale } = _withRecentChanges(apiBudget, holder && holder.budgets[apiBudget.budgetDate]);
    if (isStale) {
      staleKeys.add(holder.key);
    }
    return budget;
  });
  staleKeys.forEach(_removeKey);
  return budgets;
}

export function getBudgetWithRecentChanges(apiBudget) {
  return getBudgetsWithRecentChanges([apiBudget])[0];
}
